import { useEffect, useState } from 'react';
import { Storage, StorageReadOnlyFieldError } from '../lib/storage';
import { playerSurfaceActive } from '../lib/api/customerContext';
import { isApiMode } from '../lib/api/baseUrl';
import { renameClub } from '../lib/api/organizationName';
import { acknowledgeRename, actingOrganizationId, withCanonicalName } from './canonicalClubName';

/**
 * What a save actually accomplished (#775 re-review).
 *
 * A STRUCTURED RESULT, because the form has to know. Its compare-and-swap
 * baseline is the name it opened on, and that has to advance exactly when
 * THIS form's rename committed — not on a background prop change (which would
 * reopen the stale-form window) and not on an outcome nobody knows (which
 * would advance past a rename that may never have happened).
 *
 * Returning nothing is what left a renamed form permanently stale: every
 * later save re-sent the original expectation, so changing a phone number
 * after renaming the club answered 409 until somebody reloaded.
 */
export interface SaveClubInfoResult {
  /**
   * The name the organization DEFINITELY has now, when this save renamed it.
   * Null when nothing was renamed, when the rename was refused, and when the
   * outcome is unknown — the three cases a baseline must not advance on.
   *
   * Set even if the blob write then failed: the rename committed, and the form
   * must be able to retry the rest without asking to rename again.
   */
  acceptedName: string | null;
  /** Did `club_info_v1` land? */
  stored: boolean;
}

/** Club identity + defaults (name/logo and the default court count). */
export interface ClubInfo {
  name: string;
  address: string;
  phone: string;
  /** Treningssenter / hall. */
  venue: string;
  /** Number of courts — the default in every tournament setup. */
  courts: number;
  /** base64-encoded logo image. */
  logoDataUrl: string;
}

export const DEFAULT_CLUB_INFO: ClubInfo = {
  name: '',
  address: '',
  phone: '',
  venue: '',
  courts: 0,
  logoDataUrl: '',
};

type ToastFn = (type: string, message: string, duration?: number) => void;

/**
 * Club info, persisted to Storage under 'club_info_v1'. The first persisted-
 * domain hook lifted out of the Padel24AppMain god component (see
 * docs/history/DECOMPOSITION_PLAN.md). clubInfo is the cleanest slice — self-contained,
 * with NO §13.20 blanket-persist flag. Load-on-mount + the optimistic save
 * (setState THEN Storage.set) are copied verbatim from the monolith; behaviour
 * is unchanged. `showToast` is injected because the save surfaces a toast.
 */
/**
 * Has the club's own settings been read yet? (#775 re-review.)
 *
 * ── WHY THIS HAD TO EXIST ──────────────────────────────────────────
 *
 * `clubInfo` starts as `DEFAULT_CLUB_INFO` — every field empty — and the real
 * values arrive from a mount effect. Nothing gated the settings screen on
 * that: `StoreProvider` creates this hook immediately, `AppShell` mounts as
 * soon as auth and scope resolve, and `HomeHubViews` renders the editor the
 * moment the view changes. On a slow read, somebody could open
 * Klubbinnstillinger, be handed a form full of blanks, and save it — writing
 * an EMPTY name over «Padel24» in the blob while `tenants.name` kept the real
 * one. The split-brain name #766 exists to remove, recreated by the screen
 * meant to fix it, and the other loaded settings discarded with it.
 *
 * Worse, it needed no typing at all: `isDirty` compares the editor's fields
 * against the parent's NEWLY LOADED ones, so the save button lit up by itself.
 *
 * I had claimed navigation made this impossible. It does not — there was no
 * readiness gate on the navigation, which is what the review found by
 * following the chain I had only assumed.
 */
export type ClubInfoStatus =
  /** The read is in flight. Nothing may be edited against defaults. */
  | 'loading'
  /** Read, or legitimately absent — a club that has never saved settings. */
  | 'ready'
  /**
   * The read FAILED. Distinct from «absent», because the difference decides
   * whether an empty form is the truth or a lie: a club with no settings may
   * safely be edited from blank, while one whose settings could not be read
   * must not be, or the save overwrites values nobody has seen.
   */
  | 'unavailable';

/**
 * The `club_info_v1` document as it is STORED — the settings without the
 * club's name (#780).
 *
 * `tenants.name` is the club's name, and `PATCH /organization/name` is the
 * only door that changes it. Since #780 step 2 the `/kv` guard refuses ANY
 * `name` key in this document, equal or not, so in API mode the field must not
 * be sent at all. A document already stored WITH a name keeps it until the club's
 * next save, and every reader still sees the organization's name meanwhile:
 * the read projection overrides it.
 *
 * ── NOT OUTSIDE API MODE ───────────────────────────────────────────
 *
 * The memory backend has no organizations, so the document's name is the only
 * one there is (`canonicalClubName()` answers null there for the same reason).
 * Dropping it would blank the club's name on every screen after a reload.
 */
export function storedClubDocument(info: ClubInfo): Partial<ClubInfo> {
  if (!isApiMode()) return info;
  const document: Partial<ClubInfo> = { ...info };
  delete document.name;
  return document;
}

export function useClubInfo(showToast: ToastFn) {
  const [clubInfo, setClubInfo] = useState<ClubInfo>(DEFAULT_CLUB_INFO);
  const [status, setStatus] = useState<ClubInfoStatus>('loading');

  // Load club info from Storage on startup; the default object stands until loaded.
  //
  // NOT in member mode (#185): `club_info_v1` is a `/kv` key, which needs a
  // club user's session. `useMemberBootstrap` supplies the same object from the
  // member's own bootstrap, where the club comes from the session.
  useEffect(() => {
    // MEMBER MODE IS «READY» IMMEDIATELY: `/kv` is a club key the member app
    // never reads, and `useMemberBootstrap` supplies the same object from the
    // member's own session (#185). Leaving it on `loading` would gate a screen
    // that is fed from somewhere else entirely.
    // #800: the player surface, with either credential. `/kv/club_info_v1` is
    // a club key; an account-only player reading it answers 401, and a 401 on
    // an authenticated request ends the session — so the old check did not
    // merely load the wrong thing for them, it signed them out.
    if (playerSurfaceActive()) {
      setStatus('ready');
      return;
    }
    (async () => {
      // ── `read`, NOT `get` (#775 re-review) ──────────────────────────
      //
      // `Storage.get` RETURNS NULL for a 404, for any non-2xx, and for a
      // thrown fetch — it logs and never rejects, which is reasonable for its
      // 28 call sites that treat null as absent. It made the `unavailable`
      // branch below dead for every failure that actually happens: a 500, a
      // 401, an outage. `get` answered null, this marked itself ready, and the
      // editor was handed a blank document to save over a real one.
      //
      // `Storage.read` exists for exactly this distinction, and its own
      // comment records the incident that produced it: «treating a failed read
      // as an empty club is how a live tournament appeared to vanish during an
      // API restart — the operator's next action overwrote a document that was
      // still in the database». Same defect class, one key over, and the tool
      // for it was already here.
      const res = await Storage.read('club_info_v1');

      if (res.status === 'failed') {
        // SAYS NOTHING about what is stored, so nothing may be edited against
        // it. This is the case the whole gate exists for.
        console.error('[Padel24] Kunne ikke laste klubb-info: lesingen feilet');
        setStatus('unavailable');
        return;
      }

      if (res.status === 'absent') {
        // A FRESH, TRUSTWORTHY «there is nothing here» — a club that has never
        // saved its settings. Editable from blank, which is the truth.
        setStatus('ready');
        return;
      }

      try {
        const parsed = JSON.parse(res.value) as Partial<ClubInfo>;
        setClubInfo((prev) => ({ ...prev, ...parsed }));
        setStatus('ready');
      } catch (e) {
        // CONTENT WE CANNOT READ is not content we may replace: something is
        // stored and we do not know what. Same answer as a failed request.
        console.error('[Padel24] Kunne ikke laste klubb-info:', e);
        setStatus('unavailable');
      }
    })();
  }, []);

  // ── SAVING, AND WHY THE NAME NO LONGER TRAVELS WITH THE REST (#766) ──
  //
  // The name is the organization's, not the blob's: the public directory
  // searches `tenants.name`, `/me/clubs` lists it, a member's session reports
  // it. Writing it only into `club_info_v1` is what made a club unfindable
  // under the name it calls itself. So a changed name goes to
  // `PATCH /organization/name` FIRST, and the rest of the settings follow.
  //
  // ── THE OPTIMISTIC UPDATE IS GONE, DELIBERATELY ──────────────────
  //
  // This used to `setClubInfo(newInfo)` before persisting anything, so a
  // failure left the screen showing a saved-looking name that no other screen
  // in the product agreed with — «a convincing optimistic *saved* message
  // while the directory is unchanged», which the #766 decision calls out by
  // name. State moves only after the write that backs it succeeded.
  //
  // ── AND THE NAME NO LONGER GOES INTO THE BLOB AT ALL (#780) ───────
  //
  // It used to be mirrored there, while the readers still took `clubInfo.name`
  // from the document. They no longer do: `GET /kv/club_info_v1` projects
  // `tenants.name` over whatever is stored, this hook projects the acting
  // organization's name on the way out, and the member bootstrap overrides
  // `info.name` with the organization's. So in API mode the document is
  // written WITHOUT `name` — while the field exists it is a place a second
  // answer can appear, and the projection means nobody would notice.
  //
  // The SCREEN still carries the name: `setClubInfo` gets the full object, so
  // nothing on it moves. See `storedClubDocument` below for the memory backend.
  const saveClubInfo = async (
    newInfo: ClubInfo,
    expectedName?: string,
  ): Promise<SaveClubInfoResult> => {
    // ── THE EXPECTATION COMES FROM THE FORM, NOT FROM HERE (#775 review) ──
    //
    // It used to be `clubInfo.name` read at save time — the LATEST render of a
    // live prop. The hook loads `club_info_v1` asynchronously and can replace
    // it while the settings screen is open, so a person who only ever saw «A»
    // could send `expectedName: B` and have the server accept it: the
    // stale-form window #774 closed on the API side, reopened from the
    // browser. The caller passes what it displayed.
    //
    // DEFAULTED for the callers that do not rename — the mount loaders and any
    // caller that changes only the other fields. A rename needs the real
    // baseline, and `wantsRename` below is false whenever the name is unchanged
    // relative to it.
    // THE PROJECTED NAME as the fallback, not the blob's. A caller that does
    // not pass an expectation (the mount loaders, a save that changes only the
    // other fields) must compare against what the organization is called, or
    // `wantsRename` would fire on a stale blob value and ask to rename the club
    // to itself.
    const previousName = expectedName ?? withCanonicalName(clubInfo).name ?? '';
    const wantsRename = isApiMode() && (newInfo.name || '') !== previousName;
    /** What the API accepted, when it was asked. Null when nothing was renamed. */
    let accepted: string | null = null;

    if (wantsRename) {
      // ── THE TRANSPORT MAY THROW, AND THIS MUST NOT (#775 review) ────
      //
      // `apiFetch` rejects on a dropped network, a timeout or an abort. That
      // rejection used to leave `saveClubInfo` — no toast, no result — and
      // `ClubSettingsView` awaited it without a `finally`, so the button
      // stayed disabled reading «Lagrer…» until a reload.
      //
      // AND THE OUTCOME IS GENUINELY UNKNOWN: the request may have committed
      // before the answer disappeared. So the message says that rather than
      // claiming either way, and points at the one action that resolves it.
      let renamed: Awaited<ReturnType<typeof renameClub>>;
      try {
        renamed = await renameClub(newInfo.name, previousName);
      } catch (e) {
        console.error('[Padel24] Klubbnavn-endringen fikk ikke svar:', e);
        showToast(
          'error',
          'Vi fikk ikke svar på navneendringen. Last siden på nytt for å se om den ble lagret.',
        );
        // NOTHING IS ACKNOWLEDGED. The rename may have committed; advancing a
        // baseline on it would let the next save overwrite a name this person
        // never saw, and refusing to advance it only costs them a reload.
        return { acceptedName: null, stored: false };
      }
      if (renamed.code !== null) {
        // NOTHING IS WRITTEN AND NOTHING MOVES ON SCREEN. The settings the
        // person typed are still in the form, which is where they can fix the
        // one that was refused.
        if (renamed.code === 'name_changed_meanwhile') {
          showToast(
            'error',
            renamed.current
              ? `Klubben heter nå «${renamed.current}». Last siden på nytt før du endrer navnet.`
              : 'Klubbnavnet ble endret av noen andre. Last siden på nytt.',
          );
        } else if (renamed.code === 'name_too_long') {
          showToast('error', 'Klubbnavnet er for langt.');
        } else if (renamed.code === 'reauthentication_required') {
          // The session is live; what has lapsed is the twelve-hour window on
          // administrative authority (#750). Saying «try again» would be wrong.
          showToast('error', 'Du må bekrefte at det er deg før du kan endre klubbnavnet.');
        } else {
          showToast('error', 'Kunne ikke endre klubbnavnet.');
        }
        return { acceptedName: null, stored: false };
      }
      // THE NAME THE SERVER ACCEPTED, not the one we sent. They agree today —
      // the form trims and so does the API — but the screen should carry what
      // the organization actually holds, so a later change to normalisation
      // cannot make it disagree with its source.
      accepted = renamed.name;
      // ── AND THE NAME'S SOURCE IS TOLD (#776 review) ───────────────
      //
      // The projection reads a SNAPSHOT — the scope list from `/auth/scopes`,
      // or the impersonation context — and without this it would put the old
      // name straight back over the one just accepted, leaving the header
      // disagreeing with the directory and settings re-opening on a stale
      // expectation.
      //
      // Done HERE, before the blob write, so it happens even if that write
      // then fails: the rename committed, and what the screen shows must
      // follow the organization rather than the document.
      acknowledgeRename(actingOrganizationId(), accepted);
    }
    const saved = accepted === null ? newInfo : { ...newInfo, name: accepted };

    try {
      await Storage.set('club_info_v1', JSON.stringify(storedClubDocument(saved)));
      setClubInfo(saved);
      showToast('success', 'Klubbinformasjon lagret');
      return { acceptedName: accepted, stored: true };
    } catch (e) {
      console.error('[Padel24] Kunne ikke lagre klubb-info:', e);
      // ── THE SCREEN IS LEFT AGREEING WITH THE WORLD ─────────────────
      //
      // A rename that already landed is TRUE — the club really is called
      // something else now, on every other screen in the product — while the
      // rest of the form was not saved. So exactly the name moves, and the
      // message says which half did not.
      //
      // The alternative of moving nothing would leave the header showing a
      // name the organization no longer has, contradicting the very toast
      // beside it; moving everything is the optimistic update this save
      // deliberately does not do.
      if (accepted !== null) setClubInfo((prev) => ({ ...prev, name: accepted as string }));
      // ── THE ONE REFUSAL THAT RETRYING CANNOT FIX (#766 slice B) ─────
      //
      // `/kv` no longer accepts a club name in this document: the name lives
      // in the organization and changes through Klubbnavn, which is audited.
      // Since #780 this client never sends one, so a refusal here means the
      // server has started owning a field this bundle does not know about —
      // and the honest instruction is still to reload, because «try again»
      // would resend the same refused document forever.
      //
      // The canonical name comes from the refusal itself, so the message can
      // say what the club is actually called rather than that something is
      // inconsistent somewhere.
      if (e instanceof StorageReadOnlyFieldError) {
        showToast(
          'error',
          e.canonical
            ? `Klubbinfoen ble ikke lagret: klubben heter «${e.canonical}». Last siden på nytt og prøv igjen.`
            : 'Klubbinfoen ble ikke lagret: klubbnavnet endres under Klubbnavn. Last siden på nytt og prøv igjen.',
        );
        return { acceptedName: accepted, stored: false };
      }
      showToast(
        'error',
        wantsRename
          ? 'Klubbnavnet ble endret, men resten av klubbinfoen ble ikke lagret.'
          : 'Kunne ikke lagre klubb-info',
      );
      // THE RENAME IS ACKNOWLEDGED EVEN THOUGH THE BLOB WRITE FAILED. It
      // committed; the form must be able to retry the rest without asking to
      // rename again, which is the recovery this whole result type exists for.
      return { acceptedName: accepted, stored: false };
    }
  };

  // ── THE NAME IS PROJECTED ON THE WAY OUT (#766) ────────────────────
  //
  // DERIVED PER RENDER rather than written into state, and that is the whole
  // point: a scope switch or an impersonation starting mid-session changes
  // which organization the portal is acting as, and a value captured at load
  // time would keep showing the previous one. There is also no ordering to get
  // wrong — the blob's `name` cannot reach a screen even for the moment
  // between the read and a projection step.
  //
  // Everything else comes from the blob unchanged. `withCanonicalName` returns
  // it untouched when there is no acting organization to read from — outside
  // API mode, before a scope is selected — because inventing a name there
  // would blank the one the blob does have.
  const projected = withCanonicalName(clubInfo);

  // `setClubInfo` is exposed for the MOUNT LOADERS only — the club one reads
  // `club_info_v1`, the member one takes it from `/member/bootstrap` (#185).
  // Everything else goes through `saveClubInfo`, which also persists.
  return { clubInfo: projected, clubInfoStatus: status, setClubInfo, saveClubInfo };
}
